/**
 * Global Email Popup
 *
 * Salesforce Data Cloud (360) personalisation:
 * Set Theme settings → Global Email Popup → Personalisation ID.
 * See docs/global-popup-data-cloud-attributes.md for Content Zone keys.
 */
const globalEmailPopupIsShiels = () => window.STORE_CONTEXT?.isShiels === true;

if (!customElements.get("global-email-popup")) {
  const personalisationCache = new Map();
  const SDK_MAX_WAIT_MS = 15000;
  const SDK_POLL_INTERVAL_MS = 100;
  const IMAGE_PRELOAD_TIMEOUT_MS = 5000;
  const REOPEN_DISMISS_STORAGE_KEY = "global-email-popup:reopen-dismissed";
  const REOPEN_CLOSE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  const ATTR = {
    posture: ["Posture", "posture", "Variant", "Decision", "Popup_Posture"],
    showPopup: ["Show_Popup", "ShowPopup", "show_popup"],
    desktopImage: ["Desktop_Image", "DesktopImage", "Image", "Image_URL", "desktop_image", "image"],
    imagePosition: ["Image_Position", "ImagePosition", "image_position"],
    /**
     * Form-mode heading / description.
     * Marketing uses these only when Popup_Mode = Form (or the theme default is form).
     */
    heading: ["Heading", "Heading_Text", "Title", "heading"],
    description: [
      "Description",
      "Body",
      "Body_Text",
      "Content",
      "Text",
      "description",
      "content",
    ],
    /**
     * Link-out heading / subheading.
     * Marketing uses these only when Popup_Mode = Link-out. Strictly separate
     * from form heading/description so the same payload can carry both sets.
     */
    linkOutHeading: [
      "Link_Out_Heading",
      "LinkOut_Heading",
      "link_out_heading",
      "CTA_Heading",
      "Cta_Heading",
      "cta_heading",
    ],
    linkOutSubheading: [
      "Link_Out_Subheading",
      "Link_Out_Description",
      "Link_Out_Body",
      "LinkOut_Subheading",
      "link_out_subheading",
      "link_out_description",
      "link_out_body",
      "CTA_Subheading",
      "CTA_Description",
      "CTA_Body",
      "Cta_Description",
      "cta_description",
      "cta_subheading",
      "cta_body",
    ],
    buttonText: [
      "Button_Text",
      "button_Text",
      "ButtonText",
      "Submit_Button_Text",
      "CTA_Text",
      "button_text",
    ],
    thankyouHeading: [
      "Thankyou_Heading",
      "Thank_You_Heading",
      "ThankyouHeading",
      "thankyou_heading",
    ],
    thankyouSubheading: [
      "Thankyou_Subheading",
      "Thank_You_Subheading",
      "Thankyou_Description",
      "Thankyou_Body",
      "thankyou_subheading",
    ],
    thankyouClose: [
      "Thankyou_Close_Text",
      "Thank_You_Close_Button_Text",
      "Thank_You_Close_Text",
      "Thankyou_Close_Label",
      "Close_Button_Text",
      "thankyou_close_text",
    ],
    reopenButtonText: [
      "Reopen_Button_Text",
      "Reopen_Button_Label",
      "Reopen_Link_Text",
      "Reopen_Link_Label",
      "reopen_button_text",
    ],
    mode: ["Popup_Mode", "Mode", "Link_Out_Mode", "popup_mode", "mode"],
    ctaLabel: [
      "CTA_Label",
      "CTA_Text",
      "Cta_Label",
      "cta_label",
      "Button_Link_Text",
      "Link_Out_Button_Label",
      "Link_Out_Label",
      "LinkOut_Button_Label",
      "link_out_button_label",
      "link_out_label",
    ],
    ctaUrl: [
      "CTA_URL",
      "CTA_Url",
      "Cta_URL",
      "cta_url",
      "Button_Link_URL",
      "Link_Out_Button_Link",
      "Link_Out_Button_URL",
      "Link_Out_URL",
      "LinkOut_Button_Link",
      "link_out_button_link",
      "link_out_button_url",
      "link_out_url",
    ],
    ctaOpenInNewTab: [
      "CTA_Open_In_New_Tab",
      "CTA_New_Tab",
      "Cta_Open_New_Tab",
      "cta_open_new_tab",
      "Link_Out_Open_In_New_Tab",
      "Link_Out_New_Tab",
      "link_out_open_in_new_tab",
      "link_out_new_tab",
    ],
    autoOpenDelay: ["Auto_Open_Delay_Seconds", "AutoOpenDelaySeconds", "auto_open_delay_seconds"],
    firstNameLabel: ["First_Name_Label", "first_name_label"],
    lastNameLabel: ["Last_Name_Label", "last_name_label"],
    emailLabel: ["Email_Label", "email_label"],
    phoneLabel: ["Phone_Label", "phone_label"],
    dobLabel: ["DOB_Label", "Dob_Label", "dob_label"],
  };

  const pickAttr = (attributes, keys) => {
    if (!attributes) return null;
    for (const key of keys) {
      const value = attributes[key];
      if (typeof value === "string" && value.trim().length > 0) {
        return { value: value.trim(), key };
      }
    }
    return null;
  };

  /** Practical email validation (local + domain parts, max lengths). */
  const EMAIL_PATTERN =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

  const isValidEmail = (value) => {
    const email = String(value || "").trim();
    if (!email || email.length > 254) return false;
    if (!EMAIL_PATTERN.test(email)) return false;
    const at = email.lastIndexOf("@");
    if (at < 1) return false;
    const local = email.slice(0, at);
    const domain = email.slice(at + 1);
    if (local.length > 64 || !domain.includes(".")) return false;
    const tld = domain.split(".").pop();
    if (!tld || tld.length < 2) return false;
    return true;
  };

  /**
   * Normalises Australian numbers to 10 national digits (0…).
   * Accepts spaces, dashes, parentheses, +61 / 61 country code.
   */
  const normalizeAustralianPhoneDigits = (value) => {
    let raw = String(value || "").trim();
    if (!raw) return "";

    let digits = raw.replace(/[^\d+]/g, "");
    if (digits.startsWith("+")) digits = digits.slice(1);
    digits = digits.replace(/\D/g, "");

    if (digits.startsWith("61")) {
      const national = digits.slice(2);
      if (national.length === 9) digits = `0${national}`;
      else if (national.length === 10 && national.startsWith("0")) digits = national;
    }

    return digits;
  };

  const isValidAustralianPhone = (value) => {
    const digits = normalizeAustralianPhoneDigits(value);
    if (digits.length !== 10 || !digits.startsWith("0")) return false;
    // Mobile: 04XX XXX XXX
    if (/^04\d{8}$/.test(digits)) return true;
    // Landline: 02, 03, 07, 08
    if (/^0[2378]\d{8}$/.test(digits)) return true;
    // 1300 / 1800
    if (/^1[38]00\d{6}$/.test(digits)) return true;
    return false;
  };

  const formatAustralianPhoneDisplay = (value) => {
    const digits = normalizeAustralianPhoneDigits(value);
    if (!isValidAustralianPhone(value)) return String(value || "").trim();
    if (digits.startsWith("04")) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    if (/^1[38]00/.test(digits)) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  };

  /** Strip DOB input to digits and forward slashes only. */
  const sanitizeDobInput = (value) => String(value || "").replace(/[^\d/]/g, "");

  /** Format partial or full DOB entry as DD/MM/YYYY. */
  const formatDobDisplay = (value) => {
    const digits = sanitizeDobInput(value).replace(/\//g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  /** Parse DD/MM/YYYY display value to a Date, or null if invalid. */
  const parseDobDisplay = (value) => {
    const trimmed = sanitizeDobInput(value).trim();
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
    if (!match) return null;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return null;

    const d = new Date(year, month - 1, day);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
      return null;
    }
    return d;
  };

  /** Convert DD/MM/YYYY to YYYY-MM-DD for API / Data Cloud. */
  const dobDisplayToApi = (value) => {
    const trimmed = sanitizeDobInput(value).trim();
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(trimmed);
    if (!match) return trimmed;
    return `${match[3]}-${match[2]}-${match[1]}`;
  };

  /** National digits for Shiels customer API (no leading 0, no spaces). */
  const phoneToApiNationalDigits = (value) => {
    const digits = normalizeAustralianPhoneDigits(value);
    if (!digits) return "";
    return digits.startsWith("0") ? digits.slice(1) : digits;
  };

  /**
   * Creates or updates a Shopify customer via the Shiels backend API.
   * See api-use-case.md for request schema and error handling.
   */
  const submitCustomerToApi = async (formData, options = {}) => {
    const { apiUrl, countryCode = "+61", consentEmail = true, consentSms = true } = options;
    if (!apiUrl) {
      throw new Error("Customer API URL is not configured.");
    }

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        countryCode,
        phone: phoneToApiNationalDigits(formData.phone),
        dob: formData.dob,
        consent: {
          email: consentEmail,
          sms: consentSms,
        },
      }),
    });

    let data = {};
    try {
      data = await response.json();
    } catch (_e) {
      data = {};
    }

    if (!response.ok) {
      throw new Error(data.error || "Submission failed. Please try again.");
    }

    return data;
  };

  const resolvePosture = (attributes) => {
    const show = pickAttr(attributes, ATTR.showPopup);
    if (show && /^(false|0|no)$/i.test(show.value)) return "no_match";

    const raw = pickAttr(attributes, ATTR.posture);
    if (!raw) return "active";

    const v = raw.value.toLowerCase().replace(/[\s-]+/g, "_");
    if (v === "active" || v === "variant" || v === "show") return "active";
    if (v === "control" || v === "holdout") return "control";
    if (v === "suppressed" || v === "suppress" || v === "suppressed_hidden") return "suppressed";
    if (v === "hidden" || v === "hide" || v === "no_match" || v === "nomatch" || v === "hidden_no_match") {
      return "no_match";
    }
    return "active";
  };

  /**
   * T10 measurement traces (uni2) — interaction names align to ticket vocabulary.
   * Final payload shape must be confirmed with Performance Lead before go-live.
   */
  const emitPopupTrace = (eventType, context = {}) => {
    if (!globalEmailPopupIsShiels()) return;
    if (!window.SalesforceInteractions || typeof window.SalesforceInteractions.sendEvent !== "function") {
      return;
    }
    const {
      personalisationId,
      contentId,
      decisionId,
      personalizationPointId,
      personalizationPointName,
      posture,
      attributes,
      source,
    } = context;

    try {
      window.SalesforceInteractions.sendEvent({
        interaction: {
          name: `globalPopup_${eventType}`,
          eventType: "userEngagement",
          engagementType: eventType,
          source: source || "global_email_popup",
          personalization: contentId ? { id: contentId, contentId: contentId } : undefined,
          personalizationPointName,
          personalizationPointId,
          contentId,
          decisionId,
          posture,
          attributes: attributes || {},
        },
      });
      console.log("[global-email-popup] Trace", eventType, context);
    } catch (e) {
      console.warn("[global-email-popup] Trace failed", eventType, e);
    }
  };

  /**
   * Sends validated lead-capture fields to Data Cloud via SalesforceInteractions.sendEvent.
   * Uses `user.attributes` for profile / uni2 identity stitching (email is the primary key).
   */
  const sendLeadCaptureToDataCloud = (formData, traceContext = {}) => {
    if (!globalEmailPopupIsShiels()) return false;
    if (!window.SalesforceInteractions || typeof window.SalesforceInteractions.sendEvent !== "function") {
      console.warn("[global-email-popup] sendEvent not available — lead data not sent to Data Cloud");
      return false;
    }

    const userAttributes = {};
    if (formData.email) userAttributes.email = formData.email;
    if (formData.firstName) userAttributes.firstName = formData.firstName;
    if (formData.lastName) userAttributes.lastName = formData.lastName;
    if (formData.phone) userAttributes.phone = formData.phone;
    if (formData.dob) {
      userAttributes.dateOfBirth = formData.dob;
      userAttributes.dob = formData.dob;
    }

    const payload = {
      interaction: {
        name: "formEngagement",
        eventType: "userEngagement",
        engagementType: "form_submit",
        source: "global_email_popup",
        formName: "global_email_popup",
        personalization: traceContext.contentId ? { id: traceContext.contentId, contentId: traceContext.contentId } : undefined,
        personalizationPointName: traceContext.personalizationPointName,
        personalizationPointId: traceContext.personalizationPointId,
        contentId: traceContext.contentId,
        decisionId: traceContext.decisionId,
        posture: traceContext.posture,
      },
    };

    if (Object.keys(userAttributes).length > 0) {
      payload.user = { attributes: userAttributes };
    }

    try {
      window.SalesforceInteractions.sendEvent(payload);
      console.log("[global-email-popup] Lead capture sent to Data Cloud", {
        interaction: payload.interaction,
        user: payload.user,
      });
      return true;
    } catch (e) {
      console.warn("[global-email-popup] Failed to send lead capture to Data Cloud", e);
      return false;
    }
  };

  const sdkReady = () =>
    typeof window !== "undefined" &&
    window.SalesforceInteractions &&
    window.SalesforceInteractions.Personalization &&
    typeof window.SalesforceInteractions.Personalization.fetch === "function";

  const resolveSdk = () =>
    new Promise((resolve, reject) => {
      if (sdkReady()) return resolve(window.SalesforceInteractions);
      const started = Date.now();
      const interval = setInterval(() => {
        if (sdkReady()) {
          clearInterval(interval);
          resolve(window.SalesforceInteractions);
        } else if (Date.now() - started > SDK_MAX_WAIT_MS) {
          clearInterval(interval);
          reject(new Error("Salesforce Data Cloud SDK / Personalization.fetch not available"));
        }
      }, SDK_POLL_INTERVAL_MS);
    });

  const fetchPersonalisation = (personalisationId) => {
    if (personalisationCache.has(personalisationId)) {
      return personalisationCache.get(personalisationId);
    }
    const promise = resolveSdk()
      .then((SalesforceInteractions) => {
        console.log("[global-email-popup] Fetching personalisation for", personalisationId);
        return SalesforceInteractions.Personalization.fetch([personalisationId]);
      })
      .then((response) => {
        const personalizations = (response && response.personalizations) || [];
        const match = personalizations.find(
          (p) => p && p.personalizationPointName === personalisationId
        );
        if (!match) {
          throw new Error(`No personalisation returned for "${personalisationId}"`);
        }
        return match;
      });
    personalisationCache.set(personalisationId, promise);
    promise.catch(() => personalisationCache.delete(personalisationId));
    return promise;
  };

  const preloadImage = (url) =>
    new Promise((resolve) => {
      if (!url) return resolve(false);
      const img = new Image();
      let done = false;
      const finish = (ok) => {
        if (done) return;
        done = true;
        resolve(ok);
      };
      img.onload = () => finish(true);
      img.onerror = () => finish(false);
      setTimeout(() => finish(false), IMAGE_PRELOAD_TIMEOUT_MS);
      img.src = url;
    });

  class GlobalEmailPopup extends HTMLElement {
    constructor() {
      super();
      this._autoTimer = null;
      this._lastFocused = null;
      this._boundOnKeydown = this.onKeydown.bind(this);
      this._boundOnClick = this.onClick.bind(this);
      this._boundOnSubmit = this.onSubmit.bind(this);
      this._boundOnInput = this.onInput.bind(this);
      this._boundOnFocusOut = this.onFocusOut.bind(this);
      this._boundOnChange = this.onChange.bind(this);
      this._boundOnFormKeydown = this.onFormKeydown.bind(this);
      this._onReopenTriggerClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        emitPopupTrace("click", { ...this._traceContext, source: "reopen_trigger" });
        this.open({ source: "reopen-trigger" });
      };
      this._onReopenDismissClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.dismissReopenTrigger();
        emitPopupTrace("click", { ...this._traceContext, source: "reopen_dismiss" });
      };
    }

    connectedCallback() {
      this._userDismissedPopup = false;
      this._isAnimating = false;
      this._traceContext = null;

      const cooldownHours = parseFloat(this.dataset.cooldownHours || "0");
      this.cooldownMs = Math.max(0, cooldownHours) * 60 * 60 * 1000;
      this.storageKey = this.dataset.storageKey || "global-email-popup:dismissedAt";

      this.addEventListener("click", this._boundOnClick);
      this.addEventListener("input", this._boundOnInput);
      this.addEventListener("change", this._boundOnChange);
      this.addEventListener("focusout", this._boundOnFocusOut);
      this.addEventListener("keydown", this._boundOnFormKeydown);
      document.addEventListener("keydown", this._boundOnKeydown);

      this._whenChildrenReady(() => {
        this.refreshElementRefs();
        this.ensureReopenTrigger();
        this.updateSubmitButtonState();
        this.dispatchEvent(new CustomEvent("global-popup:ready", { bubbles: true }));

        const personalisationId = this.dataset.personalisationId;
        if (globalEmailPopupIsShiels() && personalisationId) {
          this.applyPersonalisation(personalisationId);
        } else {
          this.scheduleAutoOpen();
        }

        this.updateReopenTriggerVisibility();
      });
    }

    disconnectedCallback() {
      if (this._autoTimer) window.clearTimeout(this._autoTimer);
      this.removeEventListener("click", this._boundOnClick);
      this.removeEventListener("input", this._boundOnInput);
      this.removeEventListener("change", this._boundOnChange);
      this.removeEventListener("focusout", this._boundOnFocusOut);
      this.removeEventListener("keydown", this._boundOnFormKeydown);
      document.removeEventListener("keydown", this._boundOnKeydown);
    }

    /**
     * Custom-element upgrade can fire connectedCallback before our children are
     * parsed (script tag rendered just before the element). Wait until the form
     * markup is in the DOM before reading refs.
     */
    _whenChildrenReady(callback) {
      const ready = () => Boolean(this.querySelector(".global-email-popup__form-el"));
      if (ready()) {
        callback();
        return;
      }
      const observer = new MutationObserver(() => {
        if (ready()) {
          observer.disconnect();
          callback();
        }
      });
      observer.observe(this, { childList: true, subtree: true });
      window.setTimeout(() => {
        observer.disconnect();
        callback();
      }, 2000);
    }

    refreshElementRefs() {
      this.backdrop = this.querySelector(".global-email-popup__backdrop");
      this.dialog = this.querySelector(".global-email-popup__dialog");
      this.form = this.querySelector(".global-email-popup__form-el");
      this.errorEl = this.querySelector(".global-email-popup__error");
      this.stepForm = this.querySelector(".global-email-popup__step--form");
      this.stepThankyou = this.querySelector(".global-email-popup__step--thankyou");
    }

    getForm() {
      if (!this.form) this.form = this.querySelector(".global-email-popup__form-el");
      return this.form;
    }

    isFormField(el) {
      const form = this.getForm();
      return Boolean(
        el && form && form.contains(el) && el.matches("input, select, textarea")
      );
    }

    onFocusOut(e) {
      const input = e.target;
      if (!this.isFormField(input)) return;
      this.applyBlurValidation(input);
    }

    onChange(e) {
      const input = e.target;
      if (!this.isFormField(input)) return;
      this.applyBlurValidation(input);
    }

    getNamedFormControl(name) {
      if (!this.form || !name) return null;
      const escaped =
        typeof CSS !== "undefined" && typeof CSS.escape === "function"
          ? CSS.escape(name)
          : name.replace(/\\/g, "\\\\").replace(/\[/g, "\\[").replace(/\]/g, "\\]");
      return this.form.querySelector(`[name="${escaped}"]`);
    }

    isReopenTriggerEnabled() {
      return this.dataset.reopenTriggerEnabled !== "false";
    }

    isReopenDismissed() {
      try {
        return sessionStorage.getItem(REOPEN_DISMISS_STORAGE_KEY) === "1";
      } catch (e) {
        return false;
      }
    }

    dismissReopenTrigger() {
      try {
        sessionStorage.setItem(REOPEN_DISMISS_STORAGE_KEY, "1");
      } catch (e) {}
      this.hideReopenTrigger();
    }

    ensureReopenTrigger() {
      if (!this.isReopenTriggerEnabled()) {
        this.reopenTrigger = null;
        return null;
      }

      const label = (this.dataset.reopenLabel || "Sign up").trim();
      const position = this.dataset.reopenPosition || "bottom_right";
      const allowedPositions = ["bottom_left", "bottom_center", "bottom_right"];
      const pos = allowedPositions.includes(position) ? position : "bottom_right";

      let existing = document.querySelector("[data-global-email-popup-reopen]");
      if (existing && existing.matches("button")) {
        existing.remove();
        existing = null;
      }

      let bar = existing;
      if (!bar) {
        bar = document.createElement("div");
        bar.setAttribute("data-global-email-popup-reopen", "");

        const openBtn = document.createElement("button");
        openBtn.type = "button";
        openBtn.className = "global-email-popup__reopen-open";
        openBtn.setAttribute("data-popup-open", "reopen-trigger");

        const dismissBtn = document.createElement("button");
        dismissBtn.type = "button";
        dismissBtn.className = "global-email-popup__reopen-dismiss";
        dismissBtn.setAttribute("data-reopen-dismiss", "");
        dismissBtn.setAttribute("aria-label", "Dismiss offer");
        dismissBtn.innerHTML = REOPEN_CLOSE_ICON_SVG;

        bar.appendChild(openBtn);
        bar.appendChild(dismissBtn);
        document.body.appendChild(bar);

        openBtn.addEventListener("click", this._onReopenTriggerClick);
        dismissBtn.addEventListener("click", this._onReopenDismissClick);
      } else if (bar.parentElement !== document.body) {
        document.body.appendChild(bar);
      }

      bar.className = `global-email-popup__reopen-bar global-email-popup__reopen-bar--${pos}`;
      bar.classList.remove("is-visible");

      const openBtn = bar.querySelector(".global-email-popup__reopen-open");
      if (openBtn) {
        openBtn.textContent = label;
        openBtn.setAttribute("aria-label", label);
      }

      this.reopenTrigger = bar;
      return bar;
    }

    getAutoOpenDelayMs(overrideSeconds) {
      if (overrideSeconds != null && overrideSeconds !== "") {
        const sec = parseInt(overrideSeconds, 10);
        if (!Number.isNaN(sec)) return Math.max(0, sec) * 1000;
      }
      return parseInt(this.dataset.autoOpenDelayMs || "0", 10);
    }

    scheduleAutoOpen(overrideSeconds) {
      if (this._autoTimer) window.clearTimeout(this._autoTimer);
      if (this.isInCooldown()) return;
      const delayMs = this.getAutoOpenDelayMs(overrideSeconds);
      this._autoTimer = window.setTimeout(() => {
        this.open({ source: "auto" });
      }, Math.max(0, delayMs));
    }

    clearAwaitingPersonalisation() {
      this.classList.remove("global-email-popup--awaiting-personalisation");
      const skeleton = this.querySelector(".global-email-popup__skeleton");
      if (skeleton) skeleton.remove();
    }

    applyPersonalisation(personalisationId) {
      fetchPersonalisation(personalisationId)
        .then(async (personalization) => {
          const attributes = (personalization && personalization.attributes) || {};
          const hasAttributeData = Object.keys(attributes).length > 0;
          const posture = resolvePosture(attributes);

          if (personalization.personalizationId) {
            this.dataset.personalisationContentId = personalization.personalizationId;
          }
          if (personalization.decisionId) {
            this.dataset.personalisationDecisionId = personalization.decisionId;
          }
          if (personalization.personalizationPointId) {
            this.dataset.personalisationPointId = personalization.personalizationPointId;
          }
          if (personalization.personalizationPointName) {
            this.dataset.personalisationPointName = personalization.personalizationPointName;
          }
          this.dataset.personalisationPosture = posture;

          this._traceContext = {
            personalisationId,
            contentId: personalization.personalizationId,
            decisionId: personalization.decisionId,
            personalizationPointId: personalization.personalizationPointId,
            personalizationPointName: personalization.personalizationPointName,
            posture,
            attributes,
          };

          console.log("[global-email-popup] Personalisation result", {
            personalisationId,
            posture,
            attributes,
            personalization,
          });

          emitPopupTrace("decision", this._traceContext);

          this.clearAwaitingPersonalisation();

          // When a Personalisation ID is configured, the popup must only show
          // when Data Cloud actually returns attribute data. With no attributes
          // there is nothing to personalise, so we suppress rather than fall
          // back to the theme-default popup.
          if (!hasAttributeData) {
            console.warn(
              "[global-email-popup] No personalisation attribute data returned — popup will not open (Personalisation ID is set)"
            );
            emitPopupTrace("hidden_no_match", this._traceContext);
            this.hideReopenTrigger();
            this.dispatchEvent(
              new CustomEvent("global-popup:personalisation:suppressed", {
                bubbles: true,
                detail: {
                  personalisationId,
                  posture,
                  attributes,
                  personalization,
                  reason: "no_attribute_data",
                },
              })
            );
            return;
          }

          if (posture !== "active") {
            if (posture === "control") {
              emitPopupTrace("control", this._traceContext);
            } else if (posture === "suppressed") {
              emitPopupTrace("suppressed", this._traceContext);
            } else {
              emitPopupTrace("hidden_no_match", this._traceContext);
            }
            this.hideReopenTrigger();
            this.dispatchEvent(
              new CustomEvent("global-popup:personalisation:suppressed", {
                bubbles: true,
                detail: { personalisationId, posture, attributes, personalization },
              })
            );
            return;
          }

          await this.applyPersonalisationAttributes(attributes);

          if (!this.hasMeaningfulContent()) {
            console.warn("[global-email-popup] Active variant has no displayable content — popup will not open");
            return;
          }

          const delayOverride = pickAttr(attributes, ATTR.autoOpenDelay);
          this.dispatchEvent(
            new CustomEvent("global-popup:personalisation:ready", {
              bubbles: true,
              detail: {
                personalisationId,
                posture,
                contentId: personalization.personalizationId,
                decisionId: personalization.decisionId,
                personalizationPointId: personalization.personalizationPointId,
                personalizationPointName: personalization.personalizationPointName,
                attributes,
                personalization,
              },
            })
          );

          this.scheduleAutoOpen(delayOverride && delayOverride.value);
          this.updateReopenTriggerVisibility();
        })
        .catch((err) => {
          console.warn(
            "[global-email-popup] Failed to fetch personalisation — popup will not open (Personalisation ID is set)",
            personalisationId,
            err
          );
          this.clearAwaitingPersonalisation();
          // Personalisation ID is set but no data is available, so do NOT fall
          // back to the theme-default popup. The default popup only shows when
          // no Personalisation ID is configured.
          this.hideReopenTrigger();
          this.dispatchEvent(
            new CustomEvent("global-popup:personalisation:error", {
              bubbles: true,
              detail: { personalisationId, error: err },
            })
          );
        });
    }

    async applyPersonalisationAttributes(attributes) {
      // Resolve mode first so heading/description selection can be mode-aware.
      // Marketing intentionally ships both Heading/Description (for Form) and
      // Link_Out_Heading/Link_Out_Subheading (for Link-out) in the same payload.
      const resolvedMode = this.resolvePopupMode(attributes);
      const isLinkOut = resolvedMode === "cta";

      const image = pickAttr(attributes, ATTR.desktopImage);
      const position = pickAttr(attributes, ATTR.imagePosition);
      const heading = pickAttr(
        attributes,
        isLinkOut ? ATTR.linkOutHeading : ATTR.heading
      );
      const description = pickAttr(
        attributes,
        isLinkOut ? ATTR.linkOutSubheading : ATTR.description
      );
      const buttonText = pickAttr(attributes, ATTR.buttonText);
      const thankHeading = pickAttr(attributes, ATTR.thankyouHeading);
      const thankSub = pickAttr(attributes, ATTR.thankyouSubheading);
      const thankClose = pickAttr(attributes, ATTR.thankyouClose);
      const reopenLabel = pickAttr(attributes, ATTR.reopenButtonText);

      if (image) {
        await preloadImage(image.value);
        this.setBannerImage(image.value);
      }
      if (position) this.setImagePosition(position.value);

      if (heading) {
        const el = this.querySelector(".global-email-popup__heading");
        if (el) {
          const html = /<[a-z][\s\S]*>/i.test(heading.value)
            ? heading.value
            : heading.value;
          if (/<[a-z]/i.test(html)) el.innerHTML = html;
          else el.textContent = heading.value;
        }
      }
      if (description) {
        const el = this.querySelector(".global-email-popup__body");
        if (el) el.innerHTML = description.value;
      }
      if (buttonText) {
        const el = this.querySelector(".global-email-popup__submit");
        if (el) el.textContent = buttonText.value;
      }
      if (thankHeading) {
        const el = this.querySelector(".global-email-popup__thankyou-heading");
        if (el) {
          if (/<[a-z]/i.test(thankHeading.value)) el.innerHTML = thankHeading.value;
          else el.textContent = thankHeading.value;
        }
      }
      if (thankSub) {
        const el = this.querySelector(".global-email-popup__thankyou-subheading");
        if (el) el.innerHTML = thankSub.value;
      }
      if (thankClose) {
        const el = this.querySelector(".global-email-popup__thankyou-close");
        if (el) el.textContent = thankClose.value;
      }
      if (reopenLabel) {
        this.dataset.reopenLabel = reopenLabel.value;
        const bar = this.ensureReopenTrigger();
        const openBtn = bar && bar.querySelector(".global-email-popup__reopen-open");
        if (openBtn) {
          openBtn.textContent = reopenLabel.value;
          openBtn.setAttribute("aria-label", reopenLabel.value);
        }
      }

      const ctaEl = this.querySelector("[data-popup-cta]");
      if (ctaEl) {
        const ctaLabel = pickAttr(attributes, ATTR.ctaLabel);
        const ctaUrl = pickAttr(attributes, ATTR.ctaUrl);
        const ctaNewTab = pickAttr(attributes, ATTR.ctaOpenInNewTab);
        if (ctaLabel) ctaEl.textContent = ctaLabel.value;
        if (ctaUrl) ctaEl.setAttribute("href", ctaUrl.value);
        if (ctaNewTab) {
          const open = /^(true|1|yes)$/i.test(ctaNewTab.value);
          ctaEl.setAttribute("target", open ? "_blank" : "_self");
          if (open) ctaEl.setAttribute("rel", "noopener noreferrer");
          else ctaEl.removeAttribute("rel");
        }
      }

      const labelMap = [
        [ATTR.firstNameLabel, 'input[name="contact[first_name]"]'],
        [ATTR.lastNameLabel, 'input[name="contact[last_name]"]'],
        [ATTR.emailLabel, 'input[name="contact[email]"]'],
        [ATTR.phoneLabel, 'input[name="contact[phone]"]'],
        [ATTR.dobLabel, 'input[name="contact[dob]"]'],
      ];
      labelMap.forEach(([keys, inputSel]) => {
        const pickedLabel = pickAttr(attributes, keys);
        if (!pickedLabel) return;
        const input = this.querySelector(inputSel);
        const label = input && input.closest("label");
        const span = label && label.querySelector(".global-email-popup__label");
        if (span) span.textContent = pickedLabel.value;
      });
    }

    /**
     * Apply runtime mode override from Data Cloud.
     *
     * The Personalisation Template exposes `Popup_Mode` as a picklist with two
     * canonical values:
     *   - "Form"      → show lead-capture form, hide CTA
     *   - "Link-out"  → show CTA / link-out button, hide form
     *
     * Matching is case + punctuation insensitive, so legacy/alias values also
     * resolve (`form` / `lead` / `lead_capture` → form; `cta` / `linkout` /
     * `link_out` / `button` → cta). When the attribute is missing, the theme
     * mode (from the host class) is kept.
     */
    resolvePopupMode(attributes) {
      const themeMode = this.dataset.mode === "cta" ? "cta" : "form";
      const picked = pickAttr(attributes, ATTR.mode);
      let resolved = themeMode;
      let raw = null;

      if (picked) {
        raw = picked.value;
        const normalized = picked.value.toLowerCase().replace(/[\s\-]+/g, "_");
        if (
          normalized === "cta" ||
          normalized === "link_out" ||
          normalized === "linkout" ||
          normalized === "link" ||
          normalized === "button"
        ) {
          resolved = "cta";
        } else if (
          normalized === "form" ||
          normalized === "lead" ||
          normalized === "lead_capture" ||
          normalized === "leadcapture"
        ) {
          resolved = "form";
        }
      }

      this.classList.remove(
        "global-email-popup--resolved-form",
        "global-email-popup--resolved-cta"
      );
      this.classList.add(`global-email-popup--resolved-${resolved}`);
      this.dataset.modeResolved = resolved;
      this.dispatchEvent(
        new CustomEvent("global-popup:mode:resolved", {
          bubbles: true,
          detail: {
            mode: resolved,
            source: picked ? "personalisation" : "theme",
            raw,
          },
        })
      );
      return resolved;
    }

    setBannerImage(url) {
      let media = this.querySelector(".global-email-popup__media");
      if (!media && url) {
        media = document.createElement("div");
        media.className = "global-email-popup__media";
        const img = document.createElement("img");
        img.loading = "lazy";
        img.alt = "";
        media.appendChild(img);
        const inner = this.querySelector(".global-email-popup__inner");
        const content = this.querySelector(".global-email-popup__content");
        if (inner && content) inner.insertBefore(media, content);
      }
      const img = media && media.querySelector("img");
      if (img && url) {
        img.setAttribute("src", url);
        this.dialog && this.dialog.classList.add("global-email-popup__dialog--has-image");
      }
    }

    setImagePosition(position) {
      if (!this.dialog) return;
      const v = String(position).toLowerCase();
      const allowed = ["top", "left", "right"];
      const pos = allowed.includes(v) ? v : "top";
      this.dialog.classList.remove(
        "global-email-popup__dialog--image-top",
        "global-email-popup__dialog--image-left",
        "global-email-popup__dialog--image-right"
      );
      this.dialog.classList.add(`global-email-popup__dialog--image-${pos}`);
    }

    hasMeaningfulContent() {
      const heading = this.querySelector(".global-email-popup__heading");
      const body = this.querySelector(".global-email-popup__body");
      const img = this.querySelector(".global-email-popup__media img");
      const hasHeading = heading && heading.textContent.trim().length > 0 && !heading.classList.contains("visually-hidden");
      const hasBody = body && body.textContent.trim().length > 0 && !body.classList.contains("visually-hidden");
      const hasImg = img && img.getAttribute("src");
      const hasForm = !!this.form;
      return hasHeading || hasBody || hasImg || hasForm;
    }

    isInCooldown() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        if (!ts || !this.cooldownMs) return false;
        return Date.now() - ts < this.cooldownMs;
      } catch (e) {
        return false;
      }
    }

    markDismissed() {
      try {
        localStorage.setItem(this.storageKey, String(Date.now()));
      } catch (e) {}
    }

    canShowReopenTrigger() {
      if (!this.isReopenTriggerEnabled()) return false;
      if (!this.ensureReopenTrigger()) return false;
      if (this.isReopenDismissed()) return false;
      if (this._userDismissedPopup || this.isInCooldown()) return true;
      const posture = this.dataset.personalisationPosture;
      if (posture && posture !== "active") return false;
      return false;
    }

    updateReopenTriggerVisibility() {
      if (!this.isReopenTriggerEnabled()) {
        this.hideReopenTrigger();
        return;
      }
      if (this.isPopupVisible() || this._isAnimating) {
        this.hideReopenTrigger();
        return;
      }
      if (this.canShowReopenTrigger()) {
        this.showReopenTrigger();
      } else {
        this.hideReopenTrigger();
      }
    }

    showReopenTrigger() {
      if (!this.isReopenTriggerEnabled()) return;
      const btn = this.ensureReopenTrigger();
      if (!btn) return;
      btn.classList.add("is-visible");
      btn.removeAttribute("hidden");
      btn.style.display = "inline-flex";
      document.body.classList.add("global-email-popup--reopen-visible");
    }

    hideReopenTrigger() {
      const btn = this.reopenTrigger || document.querySelector("[data-global-email-popup-reopen]");
      if (!btn) return;
      btn.classList.remove("is-visible");
      btn.style.display = "none";
      document.body.classList.remove("global-email-popup--reopen-visible");
    }

    prefersReducedMotion() {
      return (
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    }

    getAnimationDurationMs() {
      if (this.prefersReducedMotion()) return 0;
      const type = this.dataset.animation || "fade_scale";
      if (type === "none") return 0;
      const ms = parseInt(this.dataset.animationDuration || "300", 10);
      return Number.isNaN(ms) ? 300 : Math.max(0, ms);
    }

    isPopupVisible() {
      return this.classList.contains("global-email-popup--visible");
    }

    waitForPopupTransition(done) {
      const duration = this.getAnimationDurationMs();
      if (!duration) {
        done();
        return;
      }

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        this.removeEventListener("transitionend", onTransitionEnd);
        window.clearTimeout(fallbackTimer);
        done();
      };

      const onTransitionEnd = (event) => {
        if (event.target !== this.dialog && event.target !== this.backdrop) return;
        finish();
      };

      this.addEventListener("transitionend", onTransitionEnd);
      const fallbackTimer = window.setTimeout(finish, duration + 80);
    }

    open({ source = "manual" } = {}) {
      if (this.isPopupVisible() || this._isAnimating) return;

      this.hideReopenTrigger();
      this.resetToFormStep();
      this._lastFocused = document.activeElement;
      this._isAnimating = true;

      this.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("global-email-popup--scroll-lock");
      document.body.classList.add("global-email-popup--scroll-lock");

      const completeOpen = () => {
        this._isAnimating = false;
        this.dialog && this.dialog.focus();
        emitPopupTrace("view", { ...this._traceContext, source: source || "open" });
        this.dispatchEvent(new CustomEvent("global-popup:opened", { bubbles: true, detail: { source } }));
      };

      const reveal = () => {
        this.classList.add("global-email-popup--visible");
        this.waitForPopupTransition(completeOpen);
      };

      if (this.getAnimationDurationMs() === 0) {
        this.classList.add("global-email-popup--visible");
        completeOpen();
        return;
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(reveal);
      });
    }

    close(reason = "close-button") {
      if (!this.isPopupVisible() || this._isAnimating) return;

      this._isAnimating = true;

      const completeClose = () => {
        this.setAttribute("aria-hidden", "true");
        document.documentElement.classList.remove("global-email-popup--scroll-lock");
        document.body.classList.remove("global-email-popup--scroll-lock");
        this.markDismissed();
        this._userDismissedPopup = true;
        this.resetToFormStep();
        this._isAnimating = false;
        this.showReopenTrigger();
        if (this._lastFocused && typeof this._lastFocused.focus === "function") {
          this._lastFocused.focus();
        }
        this.dispatchEvent(new CustomEvent("global-popup:closed", { bubbles: true, detail: { reason } }));
      };

      this.classList.remove("global-email-popup--visible");

      if (this.getAnimationDurationMs() === 0) {
        completeClose();
        return;
      }

      this.waitForPopupTransition(completeClose);
    }

    resetToFormStep() {
      this.classList.remove("global-email-popup--thankyou");
      if (this.stepForm) {
        this.stepForm.hidden = false;
        this.stepForm.classList.add("is-active");
      }
      if (this.stepThankyou) {
        this.stepThankyou.hidden = true;
        this.stepThankyou.classList.remove("is-active");
      }
      if (this.dialog) {
        this.dialog.setAttribute("aria-labelledby", "global-email-popup-title");
        const hasBody = this.querySelector("#global-email-popup-body:not(.visually-hidden)");
        if (hasBody) {
          this.dialog.setAttribute("aria-describedby", "global-email-popup-body");
        } else {
          this.dialog.removeAttribute("aria-describedby");
        }
      }
      this.resetFormFields();
      this.clearFormErrors();
    }

    getSubmitButton() {
      return this.querySelector(".global-email-popup__submit");
    }

    isSubmitReady() {
      if (!this.form) return false;

      const required = this.form.querySelectorAll("[data-required='true']");
      for (const el of required) {
        if (!String(el.value || "").trim()) return false;
      }
      return true;
    }

    updateSubmitButtonState() {
      const submit = this.getSubmitButton();
      if (!submit) return;
      const ready = this.isSubmitReady();
      submit.disabled = !ready;
      submit.setAttribute("aria-disabled", ready ? "false" : "true");
    }

    resetFormFields() {
      if (!this.form) return;
      this.form.querySelectorAll("input, select, textarea").forEach((el) => {
        if (el.type === "checkbox" || el.type === "radio") {
          el.checked = false;
        } else {
          el.value = "";
        }
      });
      this.clearFormErrors();
      this.updateSubmitButtonState();
    }

    showThankYouStep() {
      this.classList.add("global-email-popup--thankyou");
      if (this.stepForm) {
        this.stepForm.hidden = true;
        this.stepForm.classList.remove("is-active");
      }
      if (this.stepThankyou) {
        this.stepThankyou.hidden = false;
        this.stepThankyou.classList.add("is-active");
      }
      if (this.dialog) {
        this.dialog.setAttribute("aria-labelledby", "global-email-popup-thankyou-title");
        this.dialog.setAttribute("aria-describedby", "global-email-popup-thankyou-body");
      }
      const thankClose = this.querySelector(".global-email-popup__thankyou-close");
      if (thankClose) thankClose.focus();
      this.dispatchEvent(new CustomEvent("global-popup:thankyou:shown", { bubbles: true }));
    }

    setContent(content = {}) {
      const {
        heading,
        body,
        imageUrl,
        imagePosition,
        ctaText,
        thankyouHeading,
        thankyouSubheading,
        thankyouCloseText,
      } = content;

      if (typeof imageUrl === "string") this.setBannerImage(imageUrl);
      if (typeof imagePosition === "string") this.setImagePosition(imagePosition);

      const headingEl = this.querySelector(".global-email-popup__heading");
      const bodyEl = this.querySelector(".global-email-popup__body");
      const submitEl = this.querySelector(".global-email-popup__submit");
      const thankHeadingEl = this.querySelector(".global-email-popup__thankyou-heading");
      const thankSubEl = this.querySelector(".global-email-popup__thankyou-subheading");
      const thankCloseEl = this.querySelector(".global-email-popup__thankyou-close");

      if (typeof heading === "string" && headingEl) headingEl.textContent = heading;
      if (typeof body === "string" && bodyEl) bodyEl.innerHTML = body;
      if (typeof ctaText === "string" && submitEl) submitEl.textContent = ctaText;
      if (typeof thankyouHeading === "string" && thankHeadingEl) thankHeadingEl.textContent = thankyouHeading;
      if (typeof thankyouSubheading === "string" && thankSubEl) thankSubEl.innerHTML = thankyouSubheading;
      if (typeof thankyouCloseText === "string" && thankCloseEl) thankCloseEl.textContent = thankyouCloseText;
    }

    onClick(e) {
      const submitEl = e.target && e.target.closest(".global-email-popup__submit");
      if (submitEl && !submitEl.disabled) {
        e.preventDefault();
        this.onSubmit(e);
        return;
      }

      const ctaEl = e.target && e.target.closest("[data-popup-cta]");
      if (ctaEl) {
        const href = ctaEl.getAttribute("href") || "";
        const target = ctaEl.getAttribute("target") || "_self";
        emitPopupTrace("click", {
          ...this._traceContext,
          source: "cta",
          ctaUrl: href,
          ctaTarget: target,
        });
        this.dispatchEvent(
          new CustomEvent("global-popup:cta:click", {
            bubbles: true,
            detail: { url: href, target },
          })
        );
        return;
      }

      const closeEl = e.target && e.target.closest("[data-popup-close]");
      if (closeEl) {
        const reason = closeEl.getAttribute("data-popup-close") || "close-button";
        if (reason === "thankyou-close") {
          emitPopupTrace("click", { ...this._traceContext, source: "thankyou_close" });
        } else {
          // Modal close icon (X) - user dismissed the popup without converting.
          emitPopupTrace("dismiss", { ...this._traceContext, source: "close_icon", reason });
        }
        this.close(reason);
      }
    }

    onKeydown(e) {
      if (!this.isPopupVisible()) return;
      if (e.key === "Escape") {
        e.preventDefault();
        this.close("escape");
        return;
      }
      if (e.key === "Tab") {
        this.trapFocus(e);
      }
    }

    trapFocus(e) {
      if (!this.dialog) return;
      const focusable = this.dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    hideFormError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    getFieldWrapper(input) {
      return input && input.closest(".global-email-popup__field");
    }

    ensureFieldErrorEl(input) {
      const wrapper = this.getFieldWrapper(input);
      if (!wrapper) return null;
      let el = wrapper.querySelector(".global-email-popup__field-error");
      if (!el) {
        el = document.createElement("span");
        el.className = "global-email-popup__field-error";
        el.setAttribute("role", "alert");
        el.hidden = true;
        wrapper.appendChild(el);
      }
      return el;
    }

    setFieldError(input, message) {
      if (!input) return;
      const wrapper = this.getFieldWrapper(input);
      const fieldError = this.ensureFieldErrorEl(input);
      input.setAttribute("aria-invalid", "true");
      input.classList.add("global-email-popup__input--invalid");
      if (fieldError) {
        fieldError.textContent = message;
        fieldError.removeAttribute("hidden");
        fieldError.classList.add("global-email-popup__field-error--visible");
        if (!fieldError.id) {
          const slug = (input.name || input.type || "field").replace(/[^a-z0-9]+/gi, "-");
          fieldError.id = `global-popup-field-error-${slug}`;
        }
        input.setAttribute("aria-describedby", fieldError.id);
      }
      if (wrapper) wrapper.classList.add("global-email-popup__field--invalid");
    }

    clearFieldError(input) {
      if (!input) return;
      const wrapper = this.getFieldWrapper(input);
      const fieldError = wrapper && wrapper.querySelector(".global-email-popup__field-error");
      input.removeAttribute("aria-invalid");
      input.classList.remove("global-email-popup__input--invalid");
      if (fieldError) {
        fieldError.textContent = "";
        fieldError.setAttribute("hidden", "");
        fieldError.classList.remove("global-email-popup__field-error--visible");
      }
      input.removeAttribute("aria-describedby");
      if (wrapper) wrapper.classList.remove("global-email-popup__field--invalid");
    }

    clearFormErrors() {
      this.hideFormError();
      if (!this.form) return;
      this.form.querySelectorAll("input, select, textarea").forEach((el) => this.clearFieldError(el));
    }

    getEmailInput() {
      return this.getNamedFormControl("contact[email]");
    }

    getPhoneInput() {
      return this.getNamedFormControl("contact[phone]");
    }

    getFieldLabel(input) {
      if (!input) return "This field";
      const labelEl =
        input.closest("label") &&
        input.closest("label").querySelector(".global-email-popup__label");
      return labelEl ? labelEl.textContent.trim() : "This field";
    }

    isFieldRequired(input) {
      return Boolean(input && input.dataset.required === "true");
    }

    validateTextField(input) {
      if (!input) return { ok: true };
      const value = String(input.value || "").trim();
      if (!value && this.isFieldRequired(input)) {
        return {
          ok: false,
          message: `Please enter ${this.getFieldLabel(input).toLowerCase()}.`,
          input,
        };
      }
      return { ok: true };
    }

    validateDobField(dobInput) {
      if (!dobInput) return { ok: true };
      const value = sanitizeDobInput(dobInput.value).trim();
      if (!value) {
        if (this.isFieldRequired(dobInput)) {
          return {
            ok: false,
            message: `Please enter ${this.getFieldLabel(dobInput).toLowerCase()}.`,
            input: dobInput,
          };
        }
        return { ok: true };
      }

      const d = parseDobDisplay(value);
      if (!d) {
        return {
          ok: false,
          message: "Please enter a valid date of birth (DD/MM/YYYY).",
          input: dobInput,
        };
      }
      const today = new Date();
      if (d > today) {
        return { ok: false, message: "Please enter a valid date of birth.", input: dobInput };
      }
      const minAge = parseInt(dobInput.dataset.minAge || this.dataset.minAge || "0", 10);
      if (minAge > 0) {
        const age =
          today.getFullYear() -
          d.getFullYear() -
          (today < new Date(today.getFullYear(), d.getMonth(), d.getDate()) ? 1 : 0);
        if (age < minAge) {
          return {
            ok: false,
            message: `You must be at least ${minAge} years old.`,
            input: dobInput,
          };
        }
      }
      return { ok: true };
    }

    validateField(input) {
      if (!input || !input.matches("input, select, textarea")) return { ok: true };

      const type = String(input.type || "").toLowerCase();
      if (type === "email") {
        return this.validateEmailField(input, { required: this.isFieldRequired(input) });
      }
      if (type === "tel") {
        return this.validatePhoneField(input, { required: this.isFieldRequired(input) });
      }
      if (input.name === "contact[dob]") {
        return this.validateDobField(input);
      }
      return this.validateTextField(input);
    }

    applyBlurValidation(input) {
      if (!input) return;

      const result = this.validateField(input);
      if (result.ok) {
        this.clearFieldError(input);
        if (
          input.name === "contact[phone]" &&
          String(input.value || "").trim() &&
          isValidAustralianPhone(input.value)
        ) {
          input.value = formatAustralianPhoneDisplay(input.value);
        }
        if (input.name === "contact[dob]") {
          input.value = formatDobDisplay(input.value);
        }
        return;
      }
      this.setFieldError(input, result.message);
    }

    validateEmailField(emailInput, { required = false } = {}) {
      if (!emailInput) return { ok: true };
      const value = String(emailInput.value || "").trim();
      if (!value) {
        if (required || emailInput.dataset.required === "true") {
          return { ok: false, message: "Please enter your email address.", input: emailInput };
        }
        return { ok: true };
      }
      if (!isValidEmail(value)) {
        return { ok: false, message: "Please enter a valid email address.", input: emailInput };
      }
      return { ok: true };
    }

    validatePhoneField(phoneInput, { required = false } = {}) {
      if (!phoneInput) return { ok: true };
      const value = String(phoneInput.value || "").trim();
      if (!value) {
        if (required || phoneInput.dataset.required === "true") {
          return {
            ok: false,
            message: "Please enter your Australian phone number.",
            input: phoneInput,
          };
        }
        return { ok: true };
      }
      if (!isValidAustralianPhone(value)) {
        return {
          ok: false,
          message:
            "Please enter a valid Australian phone number (e.g. 04XX XXX XXX or 02 XXXX XXXX).",
          input: phoneInput,
        };
      }
      return { ok: true };
    }

    applyFieldValidationResult(result) {
      if (!result || result.ok) return true;
      this.clearFormErrors();
      if (result.input) this.setFieldError(result.input, result.message);
      this.showError(result.message);
      if (result.input && typeof result.input.focus === "function") {
        result.input.focus();
      }
      return false;
    }

    onInput(e) {
      const input = e.target;
      if (!this.isFormField(input)) return;

      if (input.name === "contact[dob]") {
        const formatted = formatDobDisplay(input.value);
        if (input.value !== formatted) {
          input.value = formatted;
        }
      }

      this.updateSubmitButtonState();
      const result = this.validateField(input);
      if (result.ok) {
        this.clearFieldError(input);
        this.hideFormError();
      }
    }

    onFormKeydown(e) {
      if (e.key !== "Enter" || e.isComposing) return;
      if (!this.isFormField(e.target)) return;
      if (e.target.matches("textarea")) return;
      const submit = this.getSubmitButton();
      if (!submit || submit.disabled) return;
      e.preventDefault();
      this.onSubmit(e);
    }

    getFormFieldValues() {
      if (!this.form) return {};
      const valueFor = (name) => {
        const el = this.getNamedFormControl(name);
        return el ? String(el.value || "").trim() : "";
      };
      const phoneRaw = valueFor("contact[phone]");
      const phone =
        phoneRaw && isValidAustralianPhone(phoneRaw)
          ? formatAustralianPhoneDisplay(phoneRaw)
          : phoneRaw;
      const dobDisplay = valueFor("contact[dob]");
      return {
        firstName: valueFor("contact[first_name]"),
        lastName: valueFor("contact[last_name]"),
        email: valueFor("contact[email]"),
        phone,
        dob: dobDisplay ? dobDisplayToApi(dobDisplay) : "",
      };
    }

    validate() {
      if (!this.form) return { ok: true };

      const fields = this.form.querySelectorAll("input, select, textarea");
      for (const el of fields) {
        const result = this.validateField(el);
        if (!result.ok) return result;
      }
      return { ok: true };
    }

    setSubmitLoading(loading) {
      const submit = this.getSubmitButton();
      if (!submit) return;
      this._submitting = loading;
      if (loading) {
        submit.disabled = true;
        submit.setAttribute("aria-disabled", "true");
        submit.setAttribute("aria-busy", "true");
        if (!submit.dataset.originalLabel) {
          submit.dataset.originalLabel = submit.textContent;
        }
        submit.textContent = "Submitting…";
        return;
      }
      submit.removeAttribute("aria-busy");
      if (submit.dataset.originalLabel) {
        submit.textContent = submit.dataset.originalLabel;
      }
      this.updateSubmitButtonState();
    }

    async onSubmit(e) {
      if (e && e.preventDefault) e.preventDefault();
      if (!this.isSubmitReady() || this._submitting) return;

      const v = this.validate();
      if (!v.ok) {
        this.applyFieldValidationResult(v);
        return;
      }

      const phoneInput = this.getPhoneInput();
      if (phoneInput && phoneInput.value.trim() && isValidAustralianPhone(phoneInput.value)) {
        phoneInput.value = formatAustralianPhoneDisplay(phoneInput.value);
      }

      emitPopupTrace("click", { ...this._traceContext, source: "submit" });
      this.dispatchEvent(new CustomEvent("global-popup:submit:click", { bubbles: true }));

      const formData = this.getFormFieldValues();
      const sentToDataCloud = sendLeadCaptureToDataCloud(formData, this._traceContext || {});

      if (!sentToDataCloud) {
        console.warn("[global-email-popup] Data Cloud sendEvent unavailable — continuing with API submit");
      }

      const apiUrl = (this.dataset.customerApiUrl || "").trim();
      let apiResult = null;

      if (apiUrl) {
        this.setSubmitLoading(true);
        try {
          apiResult = await submitCustomerToApi(formData, {
            apiUrl,
            countryCode: (this.dataset.countryCode || "+61").trim(),
            consentEmail: this.dataset.consentEmail !== "false",
            consentSms: this.dataset.consentSms !== "false",
          });
        } catch (err) {
          console.warn("[global-email-popup] Customer API submission failed", err);
          this.setSubmitLoading(false);
          this.showError(err.message || "Submission failed. Please try again.");
          return;
        }
        this.setSubmitLoading(false);
      }

      this.clearFormErrors();
      this.showThankYouStep();
      this.dispatchEvent(
        new CustomEvent("global-popup:submit:success", {
          bubbles: true,
          detail: { formData, sentToDataCloud, apiResult },
        })
      );
    }
  }

  customElements.define("global-email-popup", GlobalEmailPopup);
}
